import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { getSamples } from "./api"
import { allowUpdate } from "../../app/store"

const initialState = {
  byId: {}, // A map of samples by id
  matchesByCtx: {
    // root context powering the samples list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // samples by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "observation/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  generatedSamplesByObservationId: {},
}

/**
 * Thunk to perform async logic to fetch the samples matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchSamples = createAsyncThunk(
  "samples/fetch",
  /**
   *
   * @param filter the filters to apply to the samples search
   * @param page Pagination information
   * @param context The context from where the query is being made.  If its from the samples list view then root otherwise examples could be `observation/123`
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getSamples({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Samples reducer
 */
export const samplesSlice = createSlice({
  name: "samples",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchSamples.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchSamples.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((sample) => {
              // Add the samples to state by id (this can include ids not retrieved in this call)
              state.byId[sample.id] = {
                ...sample,
                _at: action.payload.at,
              }
              // add the ids of the samples that match from this query
              if (state.matchesByCtx[context].ids.indexOf(sample.id) === -1) {
                state.matchesByCtx[context].ids.push(sample.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchSamples.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
  },
})

// Selector for all samples
export const selectSamplesById = (state) => state.samples.byId

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectSamplesByContext(state, `rso/${id}`))
 */
export const selectSamplesByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.samples.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`samples, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectSamplesByIds(state, ["1", "2"]))
 */
export const selectSamplesByIds = createSelector(
  [
    // first input - extract value from `state`
    selectSamplesById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)
export const { clearMatches } = samplesSlice.actions

export default samplesSlice.reducer
